import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders, fetchInit } from "../utils/session";

/**
 * Invoices and receipts, whatever they are receipts for.
 *
 * The list is deliberately type-agnostic: every row carries its own
 * `type_label`, `download_url` and `send_endpoint`, so a new kind of document —
 * a proposal, a refund — appears in this UI the day the backend starts emitting
 * it, with no code here to change. Nothing in this module branches on `type`.
 */

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  return new Error(data?.msg || data?.message || data?.error || fallback);
};

/**
 * @param {"all"|"subscription"|"wallet"|string} type
 * @returns {{data: Array, total: number, owner_id: number}}
 */
export const fetchInvoices = async (userId, page = 1, limit = 15, type = "all") => {
  try {
    const res = await axios.get(`${API_BASE_URL}/invoices`, {
      params: { user_id: userId, type, page, limit },
      headers: authHeaders(),
    });
    return res.data || {};
  } catch (err) {
    throw apiError(err, "Invoices could not be loaded");
  }
};

/** Who was sent what, when, and by whom. */
export const fetchInvoiceSendLog = async (userId, page = 1, limit = 15) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/invoices/send-log`, {
      params: { user_id: userId, page, limit },
      headers: authHeaders(),
    });
    return res.data || {};
  } catch (err) {
    throw apiError(err, "The send log could not be loaded");
  }
};

/**
 * Email a document to the customer, or to an address typed in its place.
 *
 * The endpoint comes from the row rather than being built here — that is what
 * lets a document type this code has never heard of still be sendable.
 *
 * `email` is omitted when blank so the server falls back to the customer's
 * registered address; sending an empty string would be a 422 instead.
 */
export const sendInvoice = async (sendEndpoint, userId, email) => {
  const path = String(sendEndpoint || "").replace(/^\/api/, "");
  try {
    const res = await axios.post(
      `${API_BASE_URL}${path}`,
      { user_id: userId, ...(email ? { email } : {}) },
      { headers: authHeaders() },
    );
    const body = res.data || {};
    return { message: body.msg || body.message || "Sent.", sentTo: body.sent_to || email || "" };
  } catch (err) {
    throw apiError(err, "This could not be sent");
  }
};

/**
 * The host a document is actually fetched from.
 *
 * Rows carry an absolute `download_url`, and the backend has been seen to build
 * it against the wrong domain — entermark.in where this app talks to
 * entermark.ai. That is a different origin, so the browser withholds the session
 * cookie and the download comes back 401 while every other call on the screen
 * works. The path and query are the server's to decide; the host is not, so only
 * the origin is replaced.
 */
const API_ORIGIN = new URL(API_BASE_URL).origin;

const onApiOrigin = (url) => {
  try {
    const u = new URL(url, API_ORIGIN);
    return u.origin === API_ORIGIN ? u.toString() : `${API_ORIGIN}${u.pathname}${u.search}`;
  } catch {
    // Not a URL this can reason about — hand it back untouched.
    return url;
  }
};

/** The server's own explanation of a failed download, which arrives as JSON. */
const downloadError = async (res) => {
  try {
    const body = await res.clone().json();
    const msg = body?.msg || body?.message || body?.error;
    if (msg) return new Error(msg);
  } catch {
    /* A non-JSON body says nothing useful; fall through. */
  }
  return new Error("This document could not be opened");
};

/**
 * Open a document's PDF.
 *
 * Always fetched and handed over as a blob, never opened by URL. Pointing a new
 * tab straight at the API carries no session either way: on the Bearer path a
 * new tab sends no Authorization header, and on the cookie path the API is on a
 * different host, so a top-level navigation to it is cross-site and the browser
 * withholds the cookie — the server answers "No token provided". A fetch does
 * carry it, because `fetchInit()` sets `credentials: "include"` and the API
 * already allows credentialed cross-origin requests.
 */
export const openInvoice = async (url) => {
  /* Opened inside the click, before the first await. A tab created after an
     awaited fetch is no longer part of the user gesture, and popup blockers
     drop it. */
  const tab = window.open("", "_blank");
  try {
    const res = await fetch(onApiOrigin(url), fetchInit());
    if (!res.ok) throw await downloadError(res);
    const blobUrl = URL.createObjectURL(await res.blob());
    if (tab) tab.location.replace(blobUrl);
    else window.open(blobUrl, "_blank", "noopener");
    /* Revoked on a delay, not immediately: the new tab has to finish reading it
       first, and revoking straight away leaves a blank viewer. */
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    // The blank tab is the caller's doing, so it is the caller's to clean up.
    tab?.close();
    throw err;
  }
};
